import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { 
    Search, 
    User, 
    Calendar, 
    CreditCard, 
    FileText, 
    Phone, 
    Mail, 
    CheckCircle, 
    XCircle, 
    Clock,
    DollarSign,
    Activity,
    AlertCircle,
    Eye,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import axiosInstance from '../api/axiosInstance';

const PaymentHistory = () => {
    const { mrno: urlMrno } = useParams();
    const location = useLocation();
    const [mrno, setMrno] = useState(urlMrno || '');
    const [paymentData, setPaymentData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [expandedPayments, setExpandedPayments] = useState({});

    // Auto-search if MRNO is in URL
    useEffect(() => {
        if (urlMrno) {
            handleSearch();
        }
    }, [urlMrno]);

    const handleSearch = async (searchMrno = null) => {
        const searchValue = searchMrno || mrno;
        if (!searchValue.trim()) {
            setError('Please enter a valid MRNO');
            return;
        }

        setLoading(true);
        setError('');
        setPaymentData(null);
        setSearchPerformed(true);

        try {
            const response = await axiosInstance.get(`/api/history/payment-history/${searchValue.trim()}`);
            setPaymentData(response.data);
        } catch (err) {
            console.error('Payment history fetch error:', err);
            if (err.response?.status === 404) {
                setError('No patient found with this MRNO');
            } else {
                setError(err.response?.data?.error || 'Failed to fetch payment history');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'failed':
                return <XCircle className="w-5 h-5 text-red-500" />;
            case 'cancelled':
                return <Clock className="w-5 h-5 text-yellow-500" />;
            default:
                return <AlertCircle className="w-5 h-5 text-gray-500" />;
        }
    };

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
        switch (status) {
            case 'success':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'failed':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'cancelled':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const togglePaymentExpansion = (paymentId) => {
        setExpandedPayments(prev => ({
            ...prev,
            [paymentId]: !prev[paymentId]
        }));
    };

    const renderServiceDetails = (services) => {
        if (!services || services.length === 0) return null;

        return (
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Service Details ({services.length} services)
                </h4>
                <div className="space-y-2">
                    {services.map((service, index) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                            <div>
                                <p className="font-medium text-sm text-gray-900">{service.serviceName}</p>
                                <p className="text-xs text-gray-500">
                                    Qty: {service.quantity} × {formatCurrency(service.tariff)}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="font-medium text-sm text-gray-900">
                                    {formatCurrency(service.totalAmount)}
                                </p>
                                <span className={getStatusBadge(service.status)}>
                                    {service.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderAppointmentDetails = (appointment) => {
        if (!appointment) return null;

        return (
            <div className="mt-4 bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Appointment Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <p className="text-blue-600 font-medium">Visit No:</p>
                        <p className="text-blue-900">{appointment.visitNo}</p>
                    </div>
                    <div>
                        <p className="text-blue-600 font-medium">Department:</p>
                        <p className="text-blue-900">{appointment.department}</p>
                    </div>
                    <div>
                        <p className="text-blue-600 font-medium">Doctor:</p>
                        <p className="text-blue-900">{appointment.doctor}</p>
                    </div>
                    <div>
                        <p className="text-blue-600 font-medium">Date & Time:</p>
                        <p className="text-blue-900">{formatDate(appointment.appointmentDate)}</p>
                    </div>
                    {appointment.slot && (
                        <div>
                            <p className="text-blue-600 font-medium">Time Slot:</p>
                            <p className="text-blue-900">{appointment.slot}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-blue-600 font-medium">Status:</p>
                        <span className={getStatusBadge(appointment.status)}>
                            {appointment.status}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <Activity className="w-6 h-6 mr-3 text-blue-600" />
                        Payment History
                    </h1>
                    
                    {/* Search Box */}
                    <div className="flex gap-3">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={mrno}
                                onChange={(e) => setMrno(e.target.value.toUpperCase())}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter Patient MRNO"
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                disabled={loading}
                            />
                        </div>
                        <button
                            onClick={() => handleSearch()}
                            disabled={loading || !mrno.trim()}
                            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                )}

                {/* Patient Information */}
                {paymentData && (
                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <User className="w-5 h-5 mr-2 text-blue-600" />
                            Patient Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Name</p>
                                <p className="font-medium text-gray-900">{paymentData.patient.name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">MRNO</p>
                                <p className="font-medium text-gray-900">{paymentData.patient.mrno}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Gender</p>
                                <p className="font-medium text-gray-900">{paymentData.patient.gender}</p>
                            </div>
                            {paymentData.patient.contactNumber && (
                                <div>
                                    <p className="text-sm text-gray-500 flex items-center">
                                        <Phone className="w-3 h-3 mr-1" />
                                        Contact
                                    </p>
                                    <p className="font-medium text-gray-900">{paymentData.patient.contactNumber}</p>
                                </div>
                            )}
                            {paymentData.patient.emailAddress && (
                                <div>
                                    <p className="text-sm text-gray-500 flex items-center">
                                        <Mail className="w-3 h-3 mr-1" />
                                        Email
                                    </p>
                                    <p className="font-medium text-gray-900">{paymentData.patient.emailAddress}</p>
                                </div>
                            )}
                            {paymentData.patient.dob && (
                                <div>
                                    <p className="text-sm text-gray-500">Date of Birth</p>
                                    <p className="font-medium text-gray-900">
                                        {new Date(paymentData.patient.dob).toLocaleDateString('en-IN')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Payment Summary */}
                {paymentData && (
                    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                            Payment Summary
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <p className="text-2xl font-bold text-blue-600">{paymentData.summary.totalPayments}</p>
                                <p className="text-sm text-blue-800">Total Payments</p>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <p className="text-2xl font-bold text-green-600">{paymentData.summary.successfulPayments}</p>
                                <p className="text-sm text-green-800">Successful</p>
                            </div>
                            <div className="text-center p-4 bg-red-50 rounded-lg">
                                <p className="text-2xl font-bold text-red-600">{paymentData.summary.failedPayments}</p>
                                <p className="text-sm text-red-800">Failed</p>
                            </div>
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <p className="text-xl font-bold text-gray-900">{formatCurrency(paymentData.summary.totalAmountPaid)}</p>
                                <p className="text-sm text-gray-600">Total Paid</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payment History */}
                {paymentData && paymentData.paymentHistory.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <CreditCard className="w-5 h-5 mr-2 text-purple-600" />
                            Payment History ({paymentData.paymentHistory.length} transactions)
                        </h2>
                        <div className="space-y-4">
                            {paymentData.paymentHistory.map((payment, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center space-x-3">
                                            {getStatusIcon(payment.status)}
                                            <div>
                                                <p className="font-medium text-gray-900">
                                                    {payment.paymentFor === 'consultation' ? 'Consultation Payment' : 'Service Payment'}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {formatDate(payment.paymentDate)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-lg text-gray-900">
                                                {formatCurrency(payment.amountReceived || payment.amountInitiated)}
                                            </p>
                                            <span className={getStatusBadge(payment.status)}>
                                                {payment.status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                                        <div>
                                            <p className="text-gray-500">Order ID</p>
                                            <p className="font-mono text-xs text-gray-900 break-all">
                                                {payment.orderId || 'N/A'}
                                            </p>
                                        </div>
                                        {payment.paymentId_razorpay && (
                                            <div>
                                                <p className="text-gray-500">Payment ID</p>
                                                <p className="font-mono text-xs text-gray-900 break-all">
                                                    {payment.paymentId_razorpay}
                                                </p>
                                            </div>
                                        )}
                                        {payment.doctorName && (
                                            <div>
                                                <p className="text-gray-500">Doctor</p>
                                                <p className="text-gray-900">{payment.doctorName}</p>
                                            </div>
                                        )}
                                        {payment.departmentName && (
                                            <div>
                                                <p className="text-gray-500">Department</p>
                                                <p className="text-gray-900">{payment.departmentName}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Expandable Details */}
                                    {(payment.serviceDetails?.length > 0 || payment.appointmentDetails) && (
                                        <div>
                                            <button
                                                onClick={() => togglePaymentExpansion(payment.paymentId)}
                                                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                {expandedPayments[payment.paymentId] ? 'Hide Details' : 'View Details'}
                                                {expandedPayments[payment.paymentId] ? 
                                                    <ChevronUp className="w-4 h-4 ml-1" /> : 
                                                    <ChevronDown className="w-4 h-4 ml-1" />
                                                }
                                            </button>
                                            
                                            {expandedPayments[payment.paymentId] && (
                                                <div>
                                                    {renderServiceDetails(payment.serviceDetails)}
                                                    {renderAppointmentDetails(payment.appointmentDetails)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* No Results */}
                {paymentData && paymentData.paymentHistory.length === 0 && searchPerformed && (
                    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment History Found</h3>
                        <p className="text-gray-500">
                            {paymentData.message || 'No payments have been made for this patient.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentHistory;
